<template>
  <div class="space-y-4">
    <!-- How it works -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 space-y-2">
      <p class="font-semibold text-sm">{{ infoTitle }}</p>
      <div class="grid grid-cols-1 gap-3" :class="infoGridClass">
        <div v-if="showScoreSheets">
          <p class="font-medium mb-1"><i class="pi pi-file-excel text-green-600"></i> Score Sheets</p>
          <ul class="list-disc pl-4 space-y-0.5">
            <li>One Excel file with <strong>one sheet per course/subject</strong>.</li>
            <li>Same format as individual score sheet downloads — ready for score entry.</li>
            <li>Requires a <strong>result template</strong> assigned to the programme/classroom.</li>
            <li>Students sorted by <strong>matric number</strong>.</li>
          </ul>
        </div>
        <div v-if="showCbt">
          <p class="font-medium mb-1"><i class="pi pi-file-excel text-blue-600"></i> CBT Scores</p>
          <ul class="list-disc pl-4 space-y-0.5">
            <li>One Excel file with <strong>one sheet per course</strong>.</li>
            <li>Only <strong>completed (submitted)</strong> test sessions are included.</li>
            <li>Latest attempt used when a student has multiple sessions.</li>
            <li>Unlinked tests (not linked to a course) go to an <strong>"Unlinked Tests"</strong> sheet.</li>
          </ul>
        </div>
        <div v-if="showMerged">
          <p class="font-medium mb-1"><i class="pi pi-bolt text-amber-600"></i> Score Sheets + CBT (Merged)</p>
          <ul class="list-disc pl-4 space-y-0.5">
            <li>Same as score sheets, but <strong>CBT scores pre-filled</strong> automatically.</li>
            <li>Linked CBT tests fill the correct assessment column with <strong>scaled scores</strong>.</li>
            <li>Unlinked CBT tests appear in an extra <strong>"CBT Score (unlinked)"</strong> column.</li>
            <li>Students with CBT scores but <strong>not enrolled</strong> are flagged at the bottom.</li>
          </ul>
        </div>
      </div>
      <p class="text-blue-600">Select filters and click a <strong>Preview</strong> button to see what will be included before downloading.</p>
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Academic Calendar *</label>
        <AcademicCalendarSelect v-model="selectedCalendarId" size="small" placeholder="Select Session/Term"
          className="w-full" @change="clearPreviews" />
      </div>

      <template v-if="isTertiary">
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Programme</label>
          <ProgrammeSelect v-model="selectedProgrammeId" size="small" placeholder="All Programmes"
            className="w-full" showClear @change="clearPreviews" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Level</label>
          <Select v-model="selectedLevel" :options="levelOptions" optionLabel="label" optionValue="classroomName"
            placeholder="All Levels" size="small" class="w-full" showClear @change="clearPreviews" />
        </div>
      </template>

      <template v-else-if="showClassroomFilter">
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">{{ labels.CLASS }} *</label>
          <Select v-model="selectedClassroomId" :options="classroomOptions" optionLabel="label" optionValue="value"
            :placeholder="`Select ${labels.CLASS}`" size="small" class="w-full" showClear filter @change="clearPreviews" />
        </div>
      </template>
    </div>

    <!-- Preview Buttons -->
    <div class="flex flex-wrap gap-2 pt-2">
      <Button v-if="showScoreSheets" label="Preview Score Sheets" icon="pi pi-eye"
        size="small" severity="secondary" outlined :loading="previewingScoreSheet" :disabled="!canExportScoreSheet"
        @click="previewScoreSheet" />

      <Button v-if="showCbt" label="Preview CBT Scores" icon="pi pi-eye"
        size="small" severity="secondary" outlined :loading="previewingCbt" :disabled="!canExportCbt"
        @click="previewCbtScores" />

      <Button v-if="showMerged" label="Preview Merged (Score + CBT)"
        icon="pi pi-eye" size="small" severity="warn" outlined
        :loading="previewingScoreSheet || previewingCbt" :disabled="!canExportScoreSheet"
        @click="previewMerged" />
    </div>

    <!-- Bulk Import -->
    <div v-if="showUpload" class="flex items-center gap-2 pt-1">
      <Button label="Upload Bulk Score Sheet" icon="pi pi-upload" size="small" severity="secondary"
        :loading="importingBulk" :disabled="!canExportScoreSheet"
        @click="triggerFileUpload" />
      <span class="text-xs text-gray-400">Upload a multi-sheet Excel file (e.g. from Download Merged)</span>
      <input ref="fileInputRef" type="file" accept=".xlsx,.xls" class="hidden" @change="onFileSelected" />
    </div>

    <!-- Info -->
    <div v-if="!selectedCalendarId" class="text-xs text-gray-400 italic">
      Select an academic calendar to enable previews.
    </div>

    <!-- Score Sheet Preview (standalone, not merged) -->
    <div v-if="showScoreSheets && scoreSheetPreview && !mergedMode" class="border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <i class="pi pi-file-excel text-green-600"></i>
          <span class="text-sm font-semibold text-gray-700">Score Sheets Preview</span>
          <span class="text-xs text-gray-500">({{ scoreSheetPreview.total_courses }} course{{ scoreSheetPreview.total_courses !== 1 ? 's' : '' }})</span>
        </div>
        <div class="flex items-center gap-2">
          <Tag v-if="!scoreSheetPreview.has_template" severity="warn" value="No template assigned" class="text-xs" />
          <Button label="Download All" icon="pi pi-download" size="small"
            :loading="loadingScoreSheet" :disabled="scoreSheetPreview.total_courses === 0 || !scoreSheetPreview.has_template"
            @click="exportBulkScoreSheet" />
        </div>
      </div>
      <div v-if="scoreSheetPreview.items.length > 0" class="max-h-64 overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left px-3 py-1.5 font-medium text-gray-500">Course</th>
              <th class="text-right px-3 py-1.5 font-medium text-gray-500">Students</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in scoreSheetPreview.items" :key="i" class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-3 py-1.5">
                <span v-if="item.course_code" class="font-medium text-gray-700">{{ item.course_code }}</span>
                {{ item.course_title }}
              </td>
              <td class="text-right px-3 py-1.5 text-gray-600">{{ item.student_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="px-3 py-3 text-xs text-gray-500 space-y-1">
        <p><i class="pi pi-info-circle text-amber-500"></i> No courses/subjects found for the selected filters.</p>
        <ul class="list-disc pl-5 text-gray-400 space-y-0.5">
          <li v-if="isTertiary">Ensure students have <strong>HOD-endorsed</strong> course registrations for this calendar.</li>
          <li v-else>Ensure students are enrolled in subjects for the selected class and calendar.</li>
          <li v-if="!scoreSheetPreview.has_template">No result template is assigned — score sheets require a template.</li>
        </ul>
      </div>
    </div>

    <!-- CBT Preview (standalone, not merged) -->
    <div v-if="showCbt && cbtPreview && !mergedMode" class="border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <i class="pi pi-file-excel text-blue-600"></i>
          <span class="text-sm font-semibold text-gray-700">CBT Scores Preview</span>
          <span class="text-xs text-gray-500">({{ cbtPreview.total_tests }} test{{ cbtPreview.total_tests !== 1 ? 's' : '' }})</span>
          <Tag v-if="cbtPreview.unlinked_count > 0" severity="warn" :value="`${cbtPreview.unlinked_count} unlinked`" class="text-xs" />
        </div>
        <Button label="Download All" icon="pi pi-download" size="small"
          :loading="loadingCbt" :disabled="cbtPreview.items.length === 0 && cbtPreview.unlinked_count === 0"
          @click="exportBulkCbtScores" />
      </div>
      <div v-if="cbtPreview.items.length > 0" class="max-h-64 overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left px-3 py-1.5 font-medium text-gray-500">Course</th>
              <th class="text-left px-3 py-1.5 font-medium text-gray-500">Tests</th>
              <th class="text-right px-3 py-1.5 font-medium text-gray-500">Students</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in cbtPreview.items" :key="i" class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-3 py-1.5">
                <span v-if="item.course_code" class="font-medium text-gray-700">{{ item.course_code }}</span>
                {{ item.course_title }}
              </td>
              <td class="px-3 py-1.5 text-gray-600">
                <div v-for="(test, j) in item.tests" :key="j" class="truncate max-w-xs">
                  {{ test.test_title }} <span class="text-gray-400">({{ test.session_count }})</span>
                </div>
              </td>
              <td class="text-right px-3 py-1.5 text-gray-600">{{ item.student_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="cbtPreview.items.length === 0 && cbtPreview.unlinked_count === 0" class="px-3 py-4 text-center text-xs text-gray-400">
        No completed CBT sessions found for the selected filters.
      </div>
      <div v-else-if="cbtPreview.items.length === 0 && cbtPreview.unlinked_count > 0" class="px-3 py-3 text-xs text-gray-500">
        <i class="pi pi-info-circle text-amber-500"></i>
        No course-linked tests found, but <strong>{{ cbtPreview.unlinked_count }}</strong> unlinked test(s) with completed sessions will be exported to an "Unlinked Tests" sheet.
      </div>
    </div>

    <!-- Merged Preview (Score Sheets + CBT) -->
    <div v-if="showMerged && mergedMode && scoreSheetPreview" class="border border-amber-300 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-3 py-2 bg-amber-50 border-b border-amber-200">
        <div class="flex items-center gap-2">
          <i class="pi pi-bolt text-amber-600"></i>
          <span class="text-sm font-semibold text-gray-700">Merged Preview — Score Sheets + CBT Scores</span>
          <span class="text-xs text-gray-500">({{ scoreSheetPreview.total_courses }} course{{ scoreSheetPreview.total_courses !== 1 ? 's' : '' }})</span>
        </div>
        <div class="flex items-center gap-2">
          <Tag v-if="!scoreSheetPreview.has_template" severity="warn" value="No template" class="text-xs" />
          <Tag v-if="cbtPreview && cbtPreview.unlinked_count > 0" severity="info" :value="`${cbtPreview.unlinked_count} unlinked CBT`" class="text-xs" />
          <Button label="Download Merged" icon="pi pi-download" size="small" severity="warn"
            :loading="loadingMerged" :disabled="scoreSheetPreview.total_courses === 0 || !scoreSheetPreview.has_template"
            @click="exportMerged" />
        </div>
      </div>
      <div v-if="scoreSheetPreview.items.length > 0" class="max-h-64 overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="bg-amber-50/50 sticky top-0">
            <tr>
              <th class="text-left px-3 py-1.5 font-medium text-gray-500">Course</th>
              <th class="text-right px-3 py-1.5 font-medium text-gray-500">Students</th>
              <th class="text-left px-3 py-1.5 font-medium text-gray-500">CBT Tests</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in mergedPreviewItems" :key="i" class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-3 py-1.5">
                <span v-if="item.course_code" class="font-medium text-gray-700">{{ item.course_code }}</span>
                {{ item.course_title }}
              </td>
              <td class="text-right px-3 py-1.5 text-gray-600">{{ item.student_count }}</td>
              <td class="px-3 py-1.5 text-gray-500">
                <template v-if="item.tests.length > 0">
                  <div v-for="(t, j) in item.tests" :key="j" class="truncate max-w-xs">
                    {{ t.test_title }} <span class="text-gray-400">({{ t.session_count }})</span>
                  </div>
                </template>
                <span v-else class="text-gray-300 italic">No CBT</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="px-3 py-3 text-xs text-gray-500">
        <i class="pi pi-info-circle text-amber-500"></i> No courses found for the selected filters.
      </div>
      <div v-if="scoreSheetPreview.items.length > 0" class="px-3 py-2 bg-amber-50/30 border-t border-amber-100 text-xs text-gray-500">
        <i class="pi pi-info-circle"></i> Linked CBT columns will show <span class="text-green-600 font-medium">✓CBT</span> headers with scaled scores. Unlinked CBT scores appear in an extra column.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useBulkExport, type BulkExportMode } from '@/composables/useBulkExport';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';

const props = withDefaults(defineProps<{
  /** Which export flavours to expose here. Defaults to all three (Results). */
  modes?: BulkExportMode[];
}>(), {
  modes: () => ['score-sheets', 'cbt-scores', 'merged'],
});

const { isTertiary } = useSchoolFeatures();
const { labels } = useSchoolLabels();
const { levelOptions } = useTertiaryLevels();
const { classrooms } = useSchoolQuery();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const {
  selectedCalendarId,
  selectedProgrammeId,
  selectedLevel,
  selectedClassroomId,
  scoreSheetPreview,
  cbtPreview,
  mergedMode,
  mergedPreviewItems,
  previewingScoreSheet,
  previewingCbt,
  loadingScoreSheet,
  loadingCbt,
  loadingMerged,
  clearPreviews,
  previewScoreSheet,
  previewCbtScores,
  previewMerged,
  exportBulkScoreSheet,
  exportBulkCbtScores,
  exportMerged,
  importBulkScoreSheet,
  importingBulk,
} = useBulkExport();

const fileInputRef = ref<HTMLInputElement | null>(null);

function triggerFileUpload() {
  fileInputRef.value?.click();
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    importBulkScoreSheet(file);
    input.value = '';
  }
}

const classroomOptions = computed(() =>
  (classrooms.value ?? [])
    .filter((c): c is NonNullable<typeof c> => c !== null)
    .map((c) => ({ label: c.name, value: c.id })),
);

// A mode is visible only when this context offers it AND the user holds the
// matching permission (backend enforces the same abilities on preview/download).
const canBulkDownloadScores = computed(() =>
  can(currentUser.value, AppPermissions.Results.Actions.BulkDownloadScores, AppPermissions.Results.Resource),
);
const canBulkDownloadCbt = computed(() =>
  can(currentUser.value, AppPermissions.Results.Actions.BulkDownloadCbt, AppPermissions.Results.Resource),
);
// Uploading scores is a write — gate it on the score-upload ability, NOT the
// (read-only) bulk-download ability. The backend enforces the same via the
// `update_student_score` policy on the importBulkScoreSheet mutation.
const canUploadScores = computed(() =>
  can(currentUser.value, AppPermissions.Results.Actions.UploadScores, AppPermissions.Results.Resource),
);

const hasMode = (m: BulkExportMode) => props.modes.includes(m);

const showScoreSheets = computed(() => hasMode('score-sheets') && canBulkDownloadScores.value);
const showCbt = computed(() => hasMode('cbt-scores') && canBulkDownloadCbt.value);
const showMerged = computed(() => hasMode('merged') && canBulkDownloadScores.value && canBulkDownloadCbt.value);
const showUpload = computed(() => hasMode('score-sheets') && canUploadScores.value);

const visibleInfoCount = computed(() =>
  [showScoreSheets.value, showCbt.value, showMerged.value].filter(Boolean).length,
);
const infoGridClass = computed(() =>
  visibleInfoCount.value >= 3 ? 'md:grid-cols-3' : visibleInfoCount.value === 2 ? 'md:grid-cols-2' : '',
);
const infoTitle = computed(() =>
  showScoreSheets.value || showMerged.value ? 'How Bulk Export Works' : 'How Bulk CBT Export Works',
);

// Score sheets need a class in non-tertiary schools; pure CBT export does not.
const showClassroomFilter = computed(() => showScoreSheets.value || showMerged.value);

const canExportScoreSheet = computed(() => {
  if (!selectedCalendarId.value) return false;
  if (!isTertiary.value && !selectedClassroomId.value) return false;
  return true;
});

const canExportCbt = computed(() => !!selectedCalendarId.value);
</script>
